import React from 'react';
import {
  FloatingContext,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
} from '@floating-ui/react';
import classNames from 'classnames';
import * as styles from './ComboBoxList.module.scss';
import { Text } from 'components/core/Typography';

type ComboBoxListProps = {
  heading?: string;
  hasListItems: boolean;
  hasPopupContent: boolean;
  children: React.ReactNode;
  context: FloatingContext;
  floatingRef: (node: HTMLElement | null) => void;
  floatingStyles: React.CSSProperties;
  floatingProps: Record<string, unknown>;
  nodeId?: string;
};

export const ComboBoxList = ({
  children,
  heading,
  hasListItems,
  hasPopupContent,
  context,
  nodeId,
  floatingRef,
  floatingStyles,
  floatingProps,
}: ComboBoxListProps) => {
  // If popover is not open then dont render any content
  if (!context.open) return null;

  return (
    <FloatingNode id={nodeId}>
      <FloatingPortal>
        <FloatingFocusManager
          modal={false}
          // Don't hide background elements
          outsideElementsInert={false}
          context={context}
          // By default the users focus is moved into the floating element and passing -1 disables this behaviour
          // We want focus to remain in the input, so users can continue typing
          initialFocus={-1}
          // Renders a visually-hidden dismiss button for screen readers
          visuallyHiddenDismiss
        >
          <div
            ref={floatingRef}
            style={floatingStyles}
            {...floatingProps}
            className={classNames(styles.List, {
              // Avoids adding a thin white line when there are no items
              [styles.ListHasItems]: hasPopupContent,
            })}
          >
            {hasListItems && heading && (
              <Text
                className={styles.ListHeading}
                size="xs"
                variant="weak"
                component="h3"
              >
                {heading}
              </Text>
            )}
            {children}
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    </FloatingNode>
  );
};
